import { FC } from 'react';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import isSameDay from 'date-fns/isSameDay';
import parseISO from 'date-fns/parseISO';
import { useIntl, FormattedMessage, FormattedTime } from 'react-intl';
import {
  Checkmark as CheckmarkIcon,
  ClockAlt as ClockIcon,
  Station as StationIcon
} from '@urbaninfrastructure/react-icons';
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridCell,
  H2,
  H1,
  Link,
  Text,
  Time
} from '../../../components';
import * as messages from '../messages';

import { systemAlertsQuery_systemAlerts } from '../../../core-types';

type Props = {
  systemAlert: systemAlertsQuery_systemAlerts;
};

type TimeRangeProps = {
  end: string | null;
  start: string;
};

const TimeRange: FC<TimeRangeProps> = ({ start, end }) => {
  const intl = useIntl();
  const now = new Date();
  const startDate = parseISO(start);
  const startsToday = isSameDay(startDate, now);

  if (!end) {
    return startsToday ? (
      <>
        <FormattedMessage {...messages.systemStatus.today} />,{' '}
        {intl.formatDate(start, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })}
      </>
    ) : (
      <>
        {intl.formatDate(start, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })}
      </>
    );
  }

  const endDate = parseISO(end);
  const startsAndEndsSameDay = isSameDay(startDate, endDate);

  if (!startsAndEndsSameDay) {
    return (
      <>
        {intl.formatDate(startDate, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })}{' '}
        -{' '}
        {intl.formatDate(endDate, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })}
      </>
    );
  }

  const durationInSeconds = differenceInSeconds(startDate, endDate);
  const lastsTheWholeDay =
    durationInSeconds === 86340 || durationInSeconds === 86399; // 00:00:00 - 23:59:00 or 00:00:00 - 23:59:59

  return lastsTheWholeDay ? (
    <>
      <FormattedMessage {...messages.systemStatus.allDay} />,{' '}
      {intl.formatDate(startDate, {
        month: 'long',
        day: 'numeric'
      })}
    </>
  ) : (
    <>
      {intl.formatDate(startDate, {
        month: 'long',
        day: 'numeric'
      })}{' '}
      {intl.formatTime(startDate, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })}{' '}
      -{' '}
      {intl.formatTime(endDate, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })}
    </>
  );
};

const SystemStatus = ({ systemAlert }: Props) => {
  if (!systemAlert) {
    return null;
  }

  const {
    dockGroups,
    incidentEndTime,
    incidentStartTime,
    resolvedAt,
    updates
  } = systemAlert;

  const gridWith = { xss: 1, md: 3 / 7, xl: 1 / 3 };

  return (
    <Container bleed fullWidth textAlign="center" mb="md">
      <Container>
        <H2 mb={6}>
          <FormattedMessage {...messages.systemStatus.title} />
        </H2>

        <H1>{systemAlert.title}</H1>

        <Text>{systemAlert.description}</Text>

        {resolvedAt !== null && resolvedAt !== undefined ? (
          <Flex alignItems="top" justifyContent="center" mt="xs">
            <CheckmarkIcon size="20px" mt={1} color="state.success" />{' '}
            <Text bold pl={2}>
              <FormattedMessage {...messages.systemStatus.concluded} />{' '}
              <Time value={parseISO(resolvedAt)} />
            </Text>
          </Flex>
        ) : (
          <Text bold mt="xs">
            <TimeRange start={incidentStartTime} end={incidentEndTime} />
          </Text>
        )}

        <Grid justifyContent="space-evenly">
          {updates && updates.length > 0 && (
            <GridCell mt="md" textAlign="left" width={gridWith}>
              <Text bold>
                <FormattedMessage {...messages.systemStatus.updates} />
              </Text>

              {updates &&
                updates.map(
                  update =>
                    update && (
                      <Text key={update.id} typoStyle="xxs" mt="xxs">
                        <Flex alignItems="top">
                          <ClockIcon size="16px" mt={1} />

                          <Text as="span" pl={1} pr={3}>
                            <FormattedTime
                              value={update.createdAt}
                              hour12={false}
                            />
                          </Text>

                          <Text as="span">{update.title}</Text>
                        </Flex>
                      </Text>
                    )
                )}
            </GridCell>
          )}

          {dockGroups && dockGroups.length > 0 && (
            <GridCell mt="md" textAlign="left" width={gridWith}>
              <Text bold>
                <FormattedMessage {...messages.systemStatus.affectedStations} />
              </Text>

              {dockGroups &&
                dockGroups.map(
                  dockGroup =>
                    dockGroup && (
                      <Text key={dockGroup.id} typoStyle="xxs" mt="xxs">
                        <Flex alignItems="top">
                          <StationIcon size="16px" mt={1} />

                          <Text as="span" pl={3}>
                            {dockGroup.title}
                          </Text>
                        </Flex>
                      </Text>
                    )
                )}
            </GridCell>
          )}
        </Grid>

        <Box mt="lg">
          <Link route="system-status" passHref>
            <Button small as="a" shape="cool" variant="secondary">
              <FormattedMessage {...messages.systemStatus.allSystemAlerts} />
            </Button>
          </Link>
        </Box>
      </Container>
    </Container>
  );
};

export default SystemStatus;
